import type {
  DevicePlatform,
  DeviceCapability,
  FoyerCommand,
  ScreenRole,
  SignagePlaybackReport,
} from "@shared/types/foyer";

/** Human label for a device platform, e.g. "philips" → "Philips". */
const PLATFORM_LABELS: Record<DevicePlatform, string> = {
  stb: "STB",
  philips: "Philips",
  tizen: "Tizen",
  webos: "webOS",
  ios: "iOS / tvOS",
  web: "Web",
};

/**
 * "5s" / "22h" / "3d" - a span of milliseconds in the shortest form that
 * still reads at a glance. No sign, no suffix - `relativeTime` below adds
 * "ago" for a past instant, `signagePlaybackLabel` reuses the same buckets
 * for "how long on this item", and a negative/non-finite input reads "-"
 * rather than a nonsense duration.
 */
function durationLabel(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return "-";
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

/**
 * "22h ago" / "never" - how old a reading is, in the shortest form that still
 * reads at a glance.
 *
 * Lives here rather than in `DevicesView.tsx` (its original home) because
 * `DeviceDetailDialog` needs the same words for the same purpose: dating the
 * `live?.*` telemetry rows so they cannot be mistaken for current values.
 * Two copies would have drifted, and the whole point is that the two surfaces
 * agree about how stale a device is.
 */
export function relativeTime(iso: string | undefined): string {
  if (!iso) return "never";
  const ms = Date.now() - Date.parse(iso);
  if (!Number.isFinite(ms)) return "-";
  return `${durationLabel(ms)} ago`;
}

export function platformLabel(platform: DevicePlatform | undefined): string {
  return platform ? PLATFORM_LABELS[platform] : "-";
}

/** Short label for a capability chip, e.g. "guestCasting" → "Casting". */
const CAPABILITY_LABELS: Record<DeviceCapability, string> = {
  launchApp: "Launch",
  switchInput: "Inputs",
  guestCasting: "Casting",
  clearGuestCredentials: "Checkout wipe",
  guestCheckIn: "Check-in signal",
  screenshot: "Screenshot",
  screenshotNative: "Screenshot (real screen)",
  logCapture: "Logs",
  logCaptureNative: "Logs (device)",
  otaApk: "OTA (APK)",
  otaApkSilent: "OTA (APK, silent)",
  otaWebBundle: "OTA",
  otaFirmware: "OTA (firmware)",
  powerControl: "Power",
  setNtp: "NTP",
  videoCall: "Video call",
  deviceOwner: "Managed",
  rebootDevice: "Reboot (device)",
  otaPlatformApp: "OTA (app)",
};

export function capabilityLabel(capability: DeviceCapability): string {
  return CAPABILITY_LABELS[capability];
}

/**
 * Whether this box was provisioned at staging or granted by hand, in words an
 * operator can act on.
 *
 * The distinction is invisible from a desk and decides three things: whether an
 * APK rollout can complete unattended, whether a video call raises a permission
 * dialog nobody in the room can answer, and whether "Reboot" can recover a
 * wedged screen. Only Android boxes can be enrolled, so every other platform
 * gets "-" rather than a "not enrolled" that reads as a fault.
 *
 * A device that has never heartbeated has no capability list at all - also "-",
 * because "we haven't heard from it" must not render as "unmanaged".
 *
 * Samsung has the same question with a different answer. There is no enrolment
 * there: a panel is privileged when Foyer is running as the signed SSSP widget
 * and unprivileged when it is a page in the panel's browser, and the two look
 * identical from a desk while deciding exactly the same three things. The
 * observable difference is `rebootDevice`, which only the widget can offer -
 * so that is what the row reports, rather than a "-" that would read as "this
 * question does not apply here" when it very much does.
 */
export function managementLabel(
  platform: DevicePlatform | undefined,
  capabilities: DeviceCapability[] | undefined
): string {
  if (platform === "tizen") {
    if (!capabilities?.length) return "-";
    return capabilities.includes("rebootDevice")
      ? "Signed widget (privileged)"
      : "Browser (unprivileged)";
  }
  if (platform !== "stb" && platform !== "philips") return "-";
  if (!capabilities?.length) return "-";
  return capabilities.includes("deviceOwner")
    ? "Managed (device owner)"
    : "Hand-held (not enrolled)";
}

/**
 * What a delivered screenshot is actually a picture of.
 *
 * This exists because the caption used to read "in-app view" on every device
 * unconditionally, which was both always-true-by-accident and useless: an engineer
 * looking at a healthy-looking Foyer screen could not tell whether the TV was
 * showing that, or a black panel, or the Norigin app, or a firmware dialog on top
 * of us. The in-app path renders our own React tree, so on a broken box it produces
 * a picture of a working one.
 *
 * `mode` is the `captureMode` the device recorded WITH that artifact, so an old
 * screenshot keeps its true provenance even after the box was re-provisioned. When
 * it is missing - every screenshot taken before this shipped - we say so rather
 * than guess from today's capabilities.
 */
export function captureModeLabel(mode: unknown): string {
  switch (typeof mode === "string" ? mode : "") {
    // `tizen` is Samsung's privileged framebuffer grab
    // (`webapis.systemcontrol.captureScreen`) - the same guarantee as the
    // Android routes, so the same caption. Without it a real panel capture fell
    // through to "capture method not recorded", understating it exactly as
    // badly as an Android caption on a Tizen row would overstate it.
    case "accessibility":
    case "screencap":
    case "legacy":
    case "tizen":
      return "the real screen, whatever app was on it";
    case "logcat":
      return "device log";
    case "in-app":
      return "in-app view only - cannot show a black screen, a crash or another app";
    default:
      return "capture method not recorded";
  }
}

/**
 * Whether this device could produce a trustworthy screenshot at all, for the
 * caption on the capture button. Distinct from {@link captureModeLabel}, which
 * describes an artifact that already exists.
 *
 * Tizen gets its own wording. It DOES have a privileged framebuffer grab
 * (`webapis.systemcontrol.captureScreen`), so silence there would read as "this
 * screenshot is the panel" - never a safe reading of an in-app render. What
 * differs is the remedy: an operator sent to "the bench" for a Samsung panel
 * would be hunting an Android capture service that does not exist on it.
 *
 * Platforms with no native route at all still get null: "in-app only" there is
 * a statement about the platform, not a fault to flag. A device that has never
 * heartbeated also gets null - "we haven't heard from it" must not render as
 * "this box is limited".
 */
export function screenshotFidelityNote(
  platform: DevicePlatform | undefined,
  capabilities: DeviceCapability[] | undefined
): string | null {
  if (!capabilities?.length) return null;
  if (capabilities.includes("screenshotNative")) return null;
  // A Tizen row used to fall through to `null` and inherit no caveat at all,
  // which read as "this screenshot is the panel" - the one reading that is
  // never safe for an in-app render. The caveat is identical; the REMEDY is
  // not, and an operator sent to "the bench" for a Samsung panel would be
  // looking for an Android capture service that does not exist there.
  if (platform === "tizen") {
    return (
      "This panel captures the Foyer page only, not the display - so a black " +
      "screen, a crashed renderer, a firmware dialog or another app in front " +
      "will not show up. The privileged grab needs the signed widget; a panel " +
      "running Foyer in its browser cannot do it."
    );
  }
  if (platform !== "stb" && platform !== "philips") return null;
  return (
    "This box captures the Foyer page only, not the panel - so a black screen, " +
    "a crashed WebView, a firmware dialog or another app in front will not show " +
    "up. Enable the capture service at the bench to fix it."
  );
}

/** Human label for a screen role. Absent role => "tv" (back-compat default). */
export function roleLabel(role: ScreenRole | undefined): string {
  if (role === "door-sign") return "Door sign";
  if (role === "signage") return "Signage";
  return "TV";
}

/**
 * `com.widevine.alpha` -> `widevine`. Anything unrecognised is shown verbatim
 * rather than mangled: a key system we did not anticipate is exactly the case
 * somebody needs to read accurately.
 */
function shortKeySystem(keySystem: string): string {
  if (keySystem === "com.widevine.alpha") return "widevine";
  if (keySystem === "com.microsoft.playready") return "playready";
  return keySystem;
}

/**
 * What a signage panel is doing, in one line an operator can act on.
 *
 * `severity` is what the fleet colours by, and it is deliberately NOT the same
 * as `state`. A screen playing a loop that has quietly lost three files to 404s
 * reports `playing`, and that is a fault an operator must see; a screen outside
 * its scheduled hours reports `idle`, and that is simply Tuesday night.
 */
export function signagePlaybackLabel(
  report: SignagePlaybackReport | undefined
): {
  text: string;
  detail: string;
  severity: "ok" | "warn" | "error";
} | null {
  if (!report) return null;
  const failed = report.failedItems ?? 0;
  const parts: string[] = [];
  if (report.playlistName) parts.push(report.playlistName);
  if (report.itemIndex && report.itemCount) {
    parts.push(`item ${report.itemIndex}/${report.itemCount}`);
  }
  // How long on the CURRENT item, and how many times it has actually
  // changed since boot - the two facts that separate "genuinely on item 10"
  // from "wedged on item 10 forty minutes ago", which `itemIndex` alone
  // cannot: see `itemStartedAt`/`transitionsSinceBoot`'s own doc comments.
  if (report.itemStartedAt) {
    const ms = Date.now() - Date.parse(report.itemStartedAt);
    parts.push(`${durationLabel(ms)} on this item`);
  }
  if (report.transitionsSinceBoot) {
    parts.push(
      `${report.transitionsSinceBoot} transition${report.transitionsSinceBoot === 1 ? "" : "s"}`
    );
  }
  if (failed > 0) {
    parts.push(`${failed} file${failed === 1 ? "" : "s"} failed to load`);
  }
  if (report.recoveries) parts.push(`recovered ${report.recoveries}x`);
  // THE BREAK FREEZE (2026-09-02, still open): a screen was seen to freeze
  // through a whole break window without ever showing the campaign, and the
  // one thing that could not be checked remotely was whether the CLIENT ever
  // believed it was in a break at all. Surfaced here so a future occurrence
  // is answerable from this table alone, without a site visit or devtools.
  if (report.breakActive) parts.push("break active");
  // A SUCCESS fact, shown beside the faults: the player offers every key
  // system it can license and the device picks, so this is the only place
  // anybody can read which CDM a given panel actually used. Shortened because
  // an operator reads "widevine", not a reverse-DNS name.
  if (report.keySystem) parts.push(shortKeySystem(report.keySystem));
  if (report.poll && report.poll !== "ok") parts.push(`poll ${report.poll}`);
  // Leads, because it is the only line that says WHICH stall this is.
  if (report.media) parts.unshift(report.media);
  // …and when nothing is on screen, the panel's own explanation leads instead.
  // "Idle" alone cannot separate a lobby outside its hours from a screen that
  // was never scheduled, and only one of those wants somebody to do something.
  if (!report.media && report.idleReason) parts.unshift(report.idleReason);

  if (report.state === "blocked") {
    return {
      text: "Not playing",
      detail: parts.join(" · ") || "Nothing could be played.",
      severity: "error",
    };
  }
  if (report.state === "idle") {
    return {
      text: "Idle",
      // Said plainly, because "idle" is the state operators most often
      // mistake for a broken screen - see the Signage triage ladder.
      detail: parts.join(" · ") || "Nothing scheduled for right now.",
      severity: failed > 0 || report.poll !== "ok" ? "warn" : "ok",
    };
  }
  // A stalled picture is not "playing", whatever the playhead believes: the
  // wall is visibly frozen, and calling that green is the exact failure this
  // whole reporting chain exists to end.
  if (report.media) {
    return { text: "Stalled", detail: parts.join(" · "), severity: "error" };
  }
  return {
    text: "Playing",
    detail: parts.join(" · "),
    severity: failed > 0 || report.recoveries ? "warn" : "ok",
  };
}

/**
 * The vendor behind a model string. Model names are the raw strings a TV
 * reports ("43HFL5114T/12"), which are too long and too many to list in a
 * table - the vendor is what an operator actually scans for.
 */
export function vendorFor(model: string): string {
  if (/Philips|HFL/i.test(model)) return "Philips";
  if (/Samsung|Tizen|HG/i.test(model)) return "Samsung";
  if (/LG|webOS/i.test(model)) return "LG";
  if (/BRAVIA|Sony/i.test(model)) return "Sony";
  return "Other";
}

/**
 * Human label for one command row in a device's history.
 *
 * ⚠️ A REBOOT NAMES WHICH REBOOT HAPPENED, and that is the whole point of this
 * function existing here rather than as three words inline.
 *
 * `App.tsx`'s handler asks `bridge.rebootDevice()` and acks with
 * `{ method: "device" | "reload" }` - its own comment says "the fleet must not
 * be told those are the same event". The fleet was told exactly that: this row
 * rendered the bare word "Reboot" beside a green `acked` chip for both.
 *
 * The two are not close. A device reboot power-cycles the box and is the only
 * thing that clears a frozen WebView. A `reload` re-runs the CURRENT document -
 * which cannot clear a wedged WebView, and on a Tizen panel cannot climb back
 * into the widget origin either, because only a genuine widget launch runs the
 * WGT bootloader.
 *
 * Cost of not saying so, hasselbacken/302 on 2026-09-09: four consecutive
 * reboot commands, four green acks, four page reloads, and an operator
 * reasonably concluding the device had been restarted four times and was
 * unrecoverable. Every one of those acks carried `method: "reload"` in the
 * database the whole time.
 *
 * A command with no result yet (queued, sent, expired) says plain "Reboot" -
 * nothing has happened yet, so there is nothing to distinguish.
 */
export function commandLabel(c: FoyerCommand): string {
  switch (c.type) {
    case "reboot": {
      const method = c.result?.method;
      if (method === "device") return "Reboot (device)";
      if (method === "reload") return "Reboot (page reload only)";
      return "Reboot";
    }
    case "unpair":
      return "Unpair";
    case "refresh_telemetry":
      return "Refresh telemetry";
    case "screenshot":
      return "Screenshot";
    case "log_capture":
      return "Capture logs";
    case "power":
      return c.payload?.on === true ? "Display on" : "Display off";
    case "show_message": {
      const body = c.payload?.body;
      return typeof body === "string" ? `Message: "${body}"` : "Message";
    }
    default:
      return c.type;
  }
}
